import { atom } from "jotai";
import { MapAtom } from "types/maps/entities";

import { EntityKind } from "../../../models/entityKind";

/**
 * Wells Map Atom Interface
 */
export interface WellsMapAtom extends MapAtom {
  entityKind: EntityKind.Well;
}

/**
 * Empty wells map state
 */
export const EMPTY_WELLS_MAP: WellsMapAtom = {
  id: "",
  legend: {
    legendItems: [],
    title: ""
  },
  entityKind: EntityKind.Well
};

/**
 * Base atom for wells map data
 * Writable atom that automatically sets entityKind to Well
 */
const wellsMapBaseAtom = atom<WellsMapAtom>(EMPTY_WELLS_MAP);

export const wellsMapAtom = atom(
  (get) => get(wellsMapBaseAtom),
  (get, set, update: Omit<WellsMapAtom, "entityKind">) => {
    set(wellsMapBaseAtom, { ...update, entityKind: EntityKind.Well });
  }
);

// ============================================================================
// Derived Atoms - Wells Map Properties
// ============================================================================

/**
 * Wells map transaction ID
 */
export const wellsTransactionIdAtom = atom((get) => get(wellsMapAtom).id);

/**
 * Wells legend items
 */
export const wellsLegendItemsAtom = atom((get) => get(wellsMapAtom).legend.legendItems);

/**
 * The filter the wells legend was grouped for
 */
export const wellsLegendFilterIdAtom = atom((get) => get(wellsMapAtom).filterId);

/**
 * Wells legend title
 */
export const wellsLegendTitleAtom = atom((get) => get(wellsMapAtom).legend.title);

/**
 * Complete wells legend object
 */
export const wellsLegendAtom = atom((get) => get(wellsMapAtom).legend);

/**
 * Total count of well legend items
 * TODO: need to rename the atom to totalWellLegendsCountAtom
 */
export const totalWellCountAtom = atom(
  (get) => get(wellsMapAtom).legend.legendItems.length
);

/**
 * Total count of wells
 */
export const totalWellsCountAtom = atom((get) => {
  const legendItems = get(wellsMapAtom).legend.legendItems;
  return legendItems
    .filter((item) => item.inFilter)
    .reduce((sum, item) => sum + (item.count || 0), 0);
});

/**
 * Check if wells data is loaded
 */
export const isWellsDataLoadedAtom = atom((get) => {
  const wellsMap = get(wellsMapAtom);
  return wellsMap.id !== "" && wellsMap.legend.legendItems.length > 0;
});
